import { Controller } from '@hotwired/stimulus'
import Imask from 'imask'
import { stimulus } from '~/init'

class AppointmentFormController extends Controller {
  static targets = [
    'form',
    'phoneField', 'phoneHint', 'phoneSuggestions', 'firstNameField', 'lastNameField', 'nameSuggestions',
    'customerTypeToggle', 'customerTypeBtn', 'individualFields', 'companyFields', 'companyNameField',
    'vehicleSelector', 'vehicleSelect', 'vehicleFields',
    'vinField', 'vinHint', 'vinLabel', 'vinModeBtn', 'decodeVinBtn',
    'makeField', 'modelField', 'yearField', 'mileageField', 'engineVolumeField', 'licensePlateField',
    'servicesSelect', 'startsAtField', 'endsAtField',
    'sidePanelContent',
    'breakHint',
    'vinCameraModal', 'vinCameraVideo', 'vinOverlayCanvas', 'vinCaptureCanvas',
    'vinCameraStatus', 'vinCaptureBtn',
    'plateSuggestions'
  ]

  static values = {
    searchUrl: String,
    suggestUrl: String,
    scheduleId: Number,
    textCustomerFound: String,
    textNewCustomer: String,
    textVisitHistory: String,
    textNoVisits: String,
    textNewCustomerTitle: String,
    textNewCustomerDescription: String,
    breakStartsAt: String,
    breakEndsAt: String,
    breakWarning: String,
    textRepeatProducts: String,
    checkVinUrl: String,
    scanUrl: String,
    textVinFound: String,
    textVinTransfer: String,
    textVinDecoding: String,
    textVinDecoded: String,
    textVinNotDecoded: String,
    suggestPlateUrl: String,
    suggestNameUrl: String
  }

  #vinStream     = null
  #vinOverlayRaf = null

  connect () {
    this.debounceTimer = null
    this.suggestTimer = null
    this.suggestAbort = null
    this.currentCustomer = null
    this.abortController = null
    this.vinDebounceTimer = null
    this.vinAbortController = null
    this.plateTimer = null
    this.plateAbort = null
    this.nameTimer = null
    this.nameAbort = null
    this.vinMode = 'vin'
    this.customerType = 'individual'
    // Ensure hidden company field is not required when starting as individual
    if (this.hasCompanyNameFieldTarget) this.companyNameFieldTarget.required = false
    this._initPhoneMask()
    this._observeTransferSignal()
    this._onDocumentClick = (e) => {
      if (this.hasPhoneSuggestionsTarget && !this.phoneSuggestionsTarget.contains(e.target) && e.target !== this.phoneFieldTarget) {
        this.hideSuggestions()
      }
      if (this.hasPlateSuggestionsTarget && !this.plateSuggestionsTarget.contains(e.target) && e.target !== this.licensePlateFieldTarget) {
        this.hidePlateSuggestions()
      }
      if (this.hasNameSuggestionsTarget && !this.nameSuggestionsTarget.contains(e.target) && e.target !== this.lastNameFieldTarget) {
        this.hideNameSuggestions()
      }
    }
    document.addEventListener('click', this._onDocumentClick)

    this._restoreDraft()

    this._onFormInput = () => this._saveDraft()
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('input', this._onFormInput)
      this.formTarget.addEventListener('change', this._onFormInput)
    }

    this._onSubmitEnd = (e) => {
      if (e.detail?.success) this._clearDraft()
    }
    document.addEventListener('turbo:submit-end', this._onSubmitEnd)
  }

  disconnect () {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.suggestTimer) clearTimeout(this.suggestTimer)
    if (this.suggestAbort) this.suggestAbort.abort()
    if (this.abortController) this.abortController.abort()
    if (this.vinDebounceTimer) clearTimeout(this.vinDebounceTimer)
    if (this.vinAbortController) this.vinAbortController.abort()
    if (this.plateTimer) clearTimeout(this.plateTimer)
    if (this.plateAbort) this.plateAbort.abort()
    if (this.nameTimer) clearTimeout(this.nameTimer)
    if (this.nameAbort) this.nameAbort.abort()
    if (this._transferObserver) this._transferObserver.disconnect()
    if (this._phoneFocusHandler && this.hasPhoneFieldTarget) {
      this.phoneFieldTarget.removeEventListener('focus', this._phoneFocusHandler)
    }
    if (this.phoneMask) this.phoneMask.destroy()
    document.removeEventListener('click', this._onDocumentClick)
    document.removeEventListener('turbo:submit-end', this._onSubmitEnd)
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener('input', this._onFormInput)
      this.formTarget.removeEventListener('change', this._onFormInput)
    }
    this.#stopVinStream()
  }

  _initPhoneMask () {
    if (!this.hasPhoneFieldTarget) return

    const el = this.phoneFieldTarget

    this.phoneMask = Imask(el, {
      mask: '+{7} (000) 000-00-00',
      lazy: false,
      placeholderChar: '_',
      overwrite: 'shift'
    })

    // If field is empty (new appointment), pre-fill +7
    if (!el.value || el.value.replace(/\D/g, '').length === 0) {
      this.phoneMask.value = '+7 (___) ___-__-__'
      this.phoneMask.updateValue()
    }

    // On focus, place cursor at the first empty digit position
    this._phoneFocusHandler = () => {
      requestAnimationFrame(() => {
        const val = el.value
        const firstUnderscore = val.indexOf('_')
        if (firstUnderscore !== -1) {
          el.setSelectionRange(firstUnderscore, firstUnderscore)
        }
      })
    }
    el.addEventListener('focus', this._phoneFocusHandler)

    this.phoneMask.on('accept', () => {
      this.onPhoneInput()
    })
  }

  // Extract raw digits from masked phone value
  get phoneDigits () {
    return (this.phoneFieldTarget.value || '').replace(/\D/g, '')
  }

  // Set phone value through IMask to maintain formatting
  setPhoneValue (phone) {
    if (this.phoneMask) {
      // Normalize: ensure digits start with 7
      let digits = (phone || '').replace(/\D/g, '')
      if (digits.startsWith('8') && digits.length === 11) {
        digits = '7' + digits.slice(1)
      }
      this.phoneMask.unmaskedValue = digits
    } else {
      this.phoneFieldTarget.value = phone
    }
  }

  _observeTransferSignal () {
    const container = document.getElementById('vehicle-transfer-signal')
    if (!container) return

    this._transferObserver = new MutationObserver(() => {
      const span = container.querySelector('span[data-phone]')
      if (!span) return

      const phone = span.dataset.phone
      this.setPhoneValue(phone)
      this.firstNameFieldTarget.value = span.dataset.firstName || ''
      this.lastNameFieldTarget.value = span.dataset.lastName || ''
      if (this.hasCompanyNameFieldTarget) {
        this.companyNameFieldTarget.value = span.dataset.companyName || ''
      }
      this.clearVinHint()

      container.innerHTML = ''

      // Trigger full customer search to load vehicles & visit history
      this.searchCustomer(phone)
    })

    this._transferObserver.observe(container, { childList: true })
  }

  onPhoneInput () {
    if (this._restoringDraft) return
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.suggestTimer) clearTimeout(this.suggestTimer)

    const digits = this.phoneDigits
    // digits includes country code "7", so 7 + 10 digits = 11 total
    // We need at least 2 digits beyond +7 to start searching
    if (digits.length < 3) {
      this.hideSuggestions()
      if (digits.length < 4) this.resetCustomerFields()
      return
    }

    if (digits.length < 4) {
      this.resetCustomerFields()
    }

    // Fetch suggestions at 3+ digits (beyond +7)
    if (this.suggestUrlValue) {
      this.suggestTimer = setTimeout(() => this.fetchSuggestions(digits), 300)
    }

    // Full search at 5+ digits
    if (digits.length >= 5) {
      this.debounceTimer = setTimeout(() => this.searchCustomer(digits), 400)
    }
  }

  async fetchSuggestions (phone) {
    if (this.suggestAbort) this.suggestAbort.abort()
    this.suggestAbort = new AbortController()

    try {
      const url = `${this.suggestUrlValue}?phone=${encodeURIComponent(phone)}`
      const response = await fetch(url, {
        signal: this.suggestAbort.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const customers = await response.json()

      if (customers.length > 0) {
        this.showSuggestions(customers)
      } else {
        this.hideSuggestions()
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[suggest]', e)
        this.hideSuggestions()
      }
    }
  }

  showSuggestions (customers) {
    if (!this.hasPhoneSuggestionsTarget) return

    const container = this.phoneSuggestionsTarget
    container.innerHTML = ''

    customers.forEach(c => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;text-align:left;padding:8px 12px;font-size:14px;cursor:pointer;border:none;background:transparent;'
      btn.onmouseenter = () => { btn.style.background = 'var(--muted)' }
      btn.onmouseleave = () => { btn.style.background = 'transparent' }

      const phoneSpan = document.createElement('span')
      phoneSpan.style.fontWeight = '500'
      phoneSpan.textContent = c.phone

      const nameSpan = document.createElement('span')
      nameSpan.style.cssText = 'font-size:12px;color:var(--muted-foreground);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;'
      nameSpan.textContent = c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ')

      btn.appendChild(phoneSpan)
      btn.appendChild(nameSpan)

      btn.addEventListener('click', (e) => {
        e.preventDefault()
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        if (this.suggestTimer) clearTimeout(this.suggestTimer)
        this.setPhoneValue(c.phone)
        // setPhoneValue fires IMask accept → onPhoneInput → new timers; clear them
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        if (this.suggestTimer) clearTimeout(this.suggestTimer)
        this.hideSuggestions()
        this.searchCustomer(c.phone)
      })

      container.appendChild(btn)
    })

    container.classList.remove('hidden')
  }

  hideSuggestions () {
    if (this.hasPhoneSuggestionsTarget) {
      this.phoneSuggestionsTarget.classList.add('hidden')
      this.phoneSuggestionsTarget.innerHTML = ''
    }
  }

  // ── Last name search ──

  onNameInput () {
    if (this._restoringDraft) return
    if (this.nameTimer) clearTimeout(this.nameTimer)

    // Клиент уже найден по телефону — подсказки по фамилии не нужны
    if (this.currentCustomer) {
      this.hideNameSuggestions()
      return
    }

    const raw = (this.lastNameFieldTarget.value || '').trim()
    if (raw.length < 2) {
      this.hideNameSuggestions()
      return
    }

    if (this.suggestNameUrlValue) {
      this.nameTimer = setTimeout(() => this.fetchNameSuggestions(raw), 300)
    }
  }

  onNameKeydown (e) {
    if (e.key === 'Escape') this.hideNameSuggestions()
  }

  async fetchNameSuggestions (name) {
    if (this.nameAbort) this.nameAbort.abort()
    this.nameAbort = new AbortController()

    try {
      const url = `${this.suggestNameUrlValue}?name=${encodeURIComponent(name)}`
      const response = await fetch(url, {
        signal: this.nameAbort.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const customers = await response.json()

      if (customers.length > 0) {
        this.showNameSuggestions(customers)
      } else {
        this.hideNameSuggestions()
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[suggest_name]', e)
        this.hideNameSuggestions()
      }
    }
  }

  showNameSuggestions (customers) {
    if (!this.hasNameSuggestionsTarget) return

    const container = this.nameSuggestionsTarget
    container.innerHTML = ''

    customers.forEach(c => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;text-align:left;padding:8px 12px;font-size:14px;cursor:pointer;border:none;background:transparent;'
      btn.onmouseenter = () => { btn.style.background = 'var(--muted)' }
      btn.onmouseleave = () => { btn.style.background = 'transparent' }

      const nameSpan = document.createElement('span')
      nameSpan.style.cssText = 'font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;'
      nameSpan.textContent = c.company_name || [c.last_name, c.first_name].filter(Boolean).join(' ')

      const phoneSpan = document.createElement('span')
      phoneSpan.style.cssText = 'font-size:12px;color:var(--muted-foreground);white-space:nowrap;'
      phoneSpan.textContent = c.phone

      btn.appendChild(nameSpan)
      btn.appendChild(phoneSpan)

      btn.addEventListener('click', (e) => {
        e.preventDefault()
        if (this.nameTimer) clearTimeout(this.nameTimer)
        this.hideNameSuggestions()
        // Подставляем телефон и запускаем полный поиск (авто, история визитов)
        this.setPhoneValue(c.phone)
        // setPhoneValue дёргает IMask accept → onPhoneInput → новые таймеры; гасим их
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        if (this.suggestTimer) clearTimeout(this.suggestTimer)
        this.hideSuggestions()
        this.searchCustomer(c.phone)
      })

      container.appendChild(btn)
    })

    container.classList.remove('hidden')
  }

  hideNameSuggestions () {
    if (this.hasNameSuggestionsTarget) {
      this.nameSuggestionsTarget.classList.add('hidden')
      this.nameSuggestionsTarget.innerHTML = ''
    }
  }

  // ── License plate search ──

  onPlateInput () {
    if (this._restoringDraft) return
    if (this.plateTimer) clearTimeout(this.plateTimer)

    const raw = (this.licensePlateFieldTarget.value || '').trim()
    if (raw.length < 2) {
      this.hidePlateSuggestions()
      return
    }

    if (this.suggestPlateUrlValue) {
      this.plateTimer = setTimeout(() => this.fetchPlateSuggestions(raw), 300)
    }
  }

  onPlateKeydown (e) {
    if (e.key === 'Escape') this.hidePlateSuggestions()
  }

  async fetchPlateSuggestions (plate) {
    if (this.plateAbort) this.plateAbort.abort()
    this.plateAbort = new AbortController()

    try {
      const url = `${this.suggestPlateUrlValue}?plate=${encodeURIComponent(plate)}`
      const response = await fetch(url, {
        signal: this.plateAbort.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const results = await response.json()

      if (results.length > 0) {
        this.showPlateSuggestions(results)
      } else {
        this.hidePlateSuggestions()
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[suggest_plate]', e)
        this.hidePlateSuggestions()
      }
    }
  }

  showPlateSuggestions (results) {
    if (!this.hasPlateSuggestionsTarget) return

    const container = this.plateSuggestionsTarget
    container.innerHTML = ''

    results.forEach(r => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;text-align:left;padding:8px 12px;font-size:14px;cursor:pointer;border:none;background:transparent;'
      btn.onmouseenter = () => { btn.style.background = 'var(--muted)' }
      btn.onmouseleave = () => { btn.style.background = 'transparent' }

      const plateSpan = document.createElement('span')
      plateSpan.style.fontWeight = '500'
      plateSpan.textContent = r.license_plate

      const infoSpan = document.createElement('span')
      infoSpan.style.cssText = 'font-size:12px;color:var(--muted-foreground);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;'
      const customerName = r.company_name || [r.first_name, r.last_name].filter(Boolean).join(' ')
      infoSpan.textContent = customerName ? `${r.display_name} · ${customerName}` : r.display_name

      btn.appendChild(plateSpan)
      btn.appendChild(infoSpan)

      btn.addEventListener('click', (e) => {
        e.preventDefault()
        if (this.plateTimer) clearTimeout(this.plateTimer)
        this.hidePlateSuggestions()
        // Fill phone and trigger full customer search
        this.setPhoneValue(r.phone)
        // Clear timers that setPhoneValue may trigger
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        if (this.suggestTimer) clearTimeout(this.suggestTimer)
        this.hideSuggestions()
        this.searchCustomer(r.phone)
      })

      container.appendChild(btn)
    })

    container.classList.remove('hidden')
  }

  hidePlateSuggestions () {
    if (this.hasPlateSuggestionsTarget) {
      this.plateSuggestionsTarget.classList.add('hidden')
      this.plateSuggestionsTarget.innerHTML = ''
    }
  }

  onPhoneKeydown (e) {
    if (e.key === 'Escape') {
      this.hideSuggestions()
    }
    // Prevent deleting the +7 prefix
    if ((e.key === 'Backspace' || e.key === 'Delete') && this.phoneMask) {
      const digits = this.phoneDigits
      if (digits.length <= 1) {
        e.preventDefault()
        // Move cursor to first input position
        const pos = this.phoneFieldTarget.value.indexOf('_')
        if (pos !== -1) {
          this.phoneFieldTarget.setSelectionRange(pos, pos)
        } else {
          this.phoneFieldTarget.setSelectionRange(4, 4) // after "+7 ("
        }
      }
    }
  }

  switchCustomerType (e) {
    const type = e.currentTarget.dataset.customerType
    this.customerType = type

    this.customerTypeBtnTargets.forEach(btn => {
      const active = btn.dataset.customerType === type
      btn.classList.toggle('bg-background', active)
      btn.classList.toggle('shadow-sm', active)
      btn.classList.toggle('text-foreground', active)
      btn.classList.toggle('text-muted-foreground', !active)
    })

    const isCompany = type === 'company'

    this.individualFieldsTargets.forEach(el => el.classList.toggle('hidden', isCompany))
    this.companyFieldsTargets.forEach(el => el.classList.toggle('hidden', !isCompany))

    // Toggle required attributes
    if (this.hasFirstNameFieldTarget) this.firstNameFieldTarget.required = !isCompany
    if (this.hasLastNameFieldTarget) this.lastNameFieldTarget.required = !isCompany
    if (this.hasCompanyNameFieldTarget) this.companyNameFieldTarget.required = isCompany

    this._saveDraft()
  }

  async searchCustomer (phone) {
    if (this.suggestAbort) this.suggestAbort.abort()
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    this.phoneHintTarget.textContent = '...'
    this.phoneHintTarget.className = 'form__hint mt-1'

    try {
      const url = `${this.searchUrlValue}?phone=${encodeURIComponent(phone)}`
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      if (data.found) {
        this.hideSuggestions()
        this.fillCustomerData(data)
        this.phoneHintTarget.textContent = `✓ ${this.textCustomerFoundValue}`
        this.phoneHintTarget.className = 'form__hint mt-1 text-green-600'
      } else {
        this.resetCustomerFields()
        this.phoneHintTarget.textContent = this.textNewCustomerValue
        this.phoneHintTarget.className = 'form__hint mt-1'
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        this.phoneHintTarget.textContent = ''
      }
    }
  }

  fillCustomerData (data) {
    this.currentCustomer = data.customer
    if (this.nameTimer) clearTimeout(this.nameTimer)
    this.hideNameSuggestions()

    // Switch to correct customer type based on found data
    const isCompany = !!data.customer.company_name
    this.customerType = isCompany ? 'company' : 'individual'

    this.customerTypeBtnTargets.forEach(btn => {
      const active = btn.dataset.customerType === this.customerType
      btn.classList.toggle('bg-background', active)
      btn.classList.toggle('shadow-sm', active)
      btn.classList.toggle('text-foreground', active)
      btn.classList.toggle('text-muted-foreground', !active)
    })

    this.individualFieldsTargets.forEach(el => el.classList.toggle('hidden', isCompany))
    this.companyFieldsTargets.forEach(el => el.classList.toggle('hidden', !isCompany))

    if (this.hasFirstNameFieldTarget) this.firstNameFieldTarget.required = !isCompany
    if (this.hasLastNameFieldTarget) this.lastNameFieldTarget.required = !isCompany
    if (this.hasCompanyNameFieldTarget) this.companyNameFieldTarget.required = isCompany

    this.firstNameFieldTarget.value = data.customer.first_name || ''
    this.lastNameFieldTarget.value = data.customer.last_name || ''
    if (this.hasCompanyNameFieldTarget) {
      this.companyNameFieldTarget.value = data.customer.company_name || ''
    }

    // Fill vehicles dropdown
    if (data.vehicles && data.vehicles.length > 0) {
      this.vehicleSelectorTarget.classList.remove('hidden')
      const select = this.vehicleSelectTarget

      // Clear existing options except the first (blank)
      while (select.options.length > 1) {
        select.remove(1)
      }

      // Reset vehicle fields when repopulating (prevents stale data)
      this.clearVehicleFields()
      this.vehicleFieldsTarget.classList.remove('hidden')

      data.vehicles.forEach(v => {
        const option = new Option(v.display_name, v.id)
        option.dataset.vin = v.vin || ''
        option.dataset.make = v.make || ''
        option.dataset.model = v.model || ''
        option.dataset.year = v.year || ''
        option.dataset.mileage = v.mileage || ''
        option.dataset.engineVolume = v.engine_volume || ''
        option.dataset.licensePlate = v.license_plate || ''
        select.add(option)
      })

      // Auto-select first vehicle
      if (data.vehicles.length === 1) {
        select.value = data.vehicles[0].id
        this.fillVehicleFields(data.vehicles[0])
      }
    } else {
      this.vehicleSelectorTarget.classList.add('hidden')
    }

    // Fill side panel with visit history
    this.renderSidePanel(data)
  }

  resetCustomerFields () {
    this.currentCustomer = null
    this.firstNameFieldTarget.value = ''
    this.lastNameFieldTarget.value = ''
    if (this.hasCompanyNameFieldTarget) this.companyNameFieldTarget.value = ''
    this.vehicleSelectorTarget.classList.add('hidden')
    this.resetVehicleSelect()
    this.clearVehicleFields()
    this.vehicleFieldsTarget.classList.remove('hidden')
    this.renderEmptySidePanel()
  }

  onVehicleSelect () {
    const select = this.vehicleSelectTarget
    const selectedOption = select.options[select.selectedIndex]

    if (!selectedOption.value) {
      this.clearVehicleFields()
      this.vehicleFieldsTarget.classList.remove('hidden')
      return
    }

    this.fillVehicleFields({
      vin: selectedOption.dataset.vin,
      make: selectedOption.dataset.make,
      model: selectedOption.dataset.model,
      year: selectedOption.dataset.year,
      mileage: selectedOption.dataset.mileage,
      engine_volume: selectedOption.dataset.engineVolume,
      license_plate: selectedOption.dataset.licensePlate
    })
    // Hide vehicle fields when existing vehicle is selected
    this.vehicleFieldsTarget.classList.add('hidden')
  }

  fillVehicleFields (vehicle) {
    this.vinFieldTarget.value = vehicle.vin || ''
    this._setReadonlyField(this.makeFieldTarget, vehicle.make || '')
    this._setReadonlyField(this.modelFieldTarget, vehicle.model || '')
    this._setReadonlyField(this.yearFieldTarget, vehicle.year || '')
    this.mileageFieldTarget.value = vehicle.mileage || ''
    this._setReadonlyField(this.engineVolumeFieldTarget, vehicle.engine_volume || '')
    if (this.hasLicensePlateFieldTarget) {
      this.licensePlateFieldTarget.value = vehicle.license_plate || ''
    }
  }

  clearVehicleFields () {
    this.vinFieldTarget.value = ''
    this._setReadonlyField(this.makeFieldTarget, '')
    this._setReadonlyField(this.modelFieldTarget, '')
    this._setReadonlyField(this.yearFieldTarget, '')
    this.mileageFieldTarget.value = ''
    this._setReadonlyField(this.engineVolumeFieldTarget, '')
    if (this.hasLicensePlateFieldTarget) {
      this.licensePlateFieldTarget.value = ''
    }
    this.clearVinHint()
  }

  resetVehicleSelect () {
    const select = this.vehicleSelectTarget
    select.value = ''
    while (select.options.length > 1) {
      select.remove(1)
    }
  }

  switchVinMode (e) {
    const btn = e.currentTarget
    this.vinMode = btn.dataset.mode

    this.vinModeBtnTargets.forEach(b => {
      const active = b.dataset.mode === this.vinMode
      b.classList.toggle('bg-background', active)
      b.classList.toggle('shadow-sm', active)
      b.classList.toggle('text-foreground', active)
      b.classList.toggle('text-muted-foreground', !active)
    })

    if (this.hasVinLabelTarget) {
      this.vinLabelTarget.textContent = btn.dataset.label || btn.textContent.trim()
    }
    if (this.hasVinFieldTarget && btn.dataset.placeholder) {
      this.vinFieldTarget.placeholder = btn.dataset.placeholder
    }

    this.clearVinHint()
  }

  onVinInput () {
    if (this.vinDebounceTimer) clearTimeout(this.vinDebounceTimer)

    const el = this.vinFieldTarget
    const pos = el.selectionStart
    el.value = el.value.toUpperCase()
    el.setSelectionRange(pos, pos)

    const vin = el.value.trim()
    this.clearVinHint()

    // Если VIN очистили или сократили — сбрасываем поля, заполненные декодером
    if (vin.length < 5) {
      this._setReadonlyField(this.makeFieldTarget, '')
      this._setReadonlyField(this.modelFieldTarget, '')
      this._setReadonlyField(this.yearFieldTarget, '')
      this._setReadonlyField(this.engineVolumeFieldTarget, '')
      return
    }

    this.vinDebounceTimer = setTimeout(() => this.checkVinExists(vin), 500)
  }

  async onDecodeVin () {
    const vin = this.vinFieldTarget.value.trim().toUpperCase()
    if (!vin) return

    if (this.vinAbortController) this.vinAbortController.abort()
    this.vinAbortController = new AbortController()

    this._setReadonlyField(this.makeFieldTarget, '')
    this._setReadonlyField(this.modelFieldTarget, '')
    this._setReadonlyField(this.yearFieldTarget, '')
    this._setReadonlyField(this.engineVolumeFieldTarget, '')

    this.vinHintTarget.textContent = this.textVinDecodingValue || '...'
    this.vinHintTarget.className = 'form__hint text-muted-foreground'

    try {
      const url = `${this.checkVinUrlValue}?vin=${encodeURIComponent(vin)}&identifier_type=${this.vinMode}`
      const response = await fetch(url, {
        signal: this.vinAbortController.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      if (data.found) {
        const currentCustomerPhone = this.phoneFieldTarget.value.trim()
        const isSameCustomer = currentCustomerPhone && data.customer.phone === currentCustomerPhone

        this.fillVehicleFieldsFromDecoded(data.vehicle)

        if (!isSameCustomer) {
          this.showVinWarning(data)
        } else {
          this.clearVinHint()
        }
      } else if (data.decoded) {
        this.fillVehicleFieldsFromDecoded(data.decoded)
      } else {
        this.vinHintTarget.textContent = this.textVinNotDecodedValue || '—'
        this.vinHintTarget.className = 'form__hint text-muted-foreground'
      }
    } catch (e) {
      if (e.name !== 'AbortError') this.clearVinHint()
    }
  }

  async checkVinExists (vin) {
    if (this.vinAbortController) this.vinAbortController.abort()
    this.vinAbortController = new AbortController()

    try {
      const url = `${this.checkVinUrlValue}?vin=${encodeURIComponent(vin)}&identifier_type=${this.vinMode}`
      const response = await fetch(url, {
        signal: this.vinAbortController.signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      if (data.found) {
        const currentCustomerPhone = this.phoneFieldTarget.value.trim()
        const isSameCustomer = currentCustomerPhone && data.customer.phone === currentCustomerPhone
        if (!isSameCustomer) this.showVinWarning(data)
      }
    } catch (e) {
      // silent
    }
  }

  fillVehicleFieldsFromDecoded (decoded) {
    this._setReadonlyField(this.makeFieldTarget, decoded.make || '')
    this._setReadonlyField(this.modelFieldTarget, decoded.model || '')
    this._setReadonlyField(this.yearFieldTarget, decoded.year || '')
    this._setReadonlyField(this.engineVolumeFieldTarget, decoded.engine_volume || '')

    const vehicleName = [decoded.make, decoded.model, decoded.year].filter(Boolean).join(' ')
    if (vehicleName) {
      const msg = (this.textVinDecodedValue || '✓ %{vehicle}').replace('%{vehicle}', vehicleName)
      this.vinHintTarget.textContent = msg
      this.vinHintTarget.className = 'form__hint text-green-600'
    } else {
      this.clearVinHint()
    }
  }

  showVinWarning (data) {
    const msg = this.textVinFoundValue
      .replace('%{vehicle}', data.vehicle.display_name)
      .replace('%{customer}', data.customer.full_name)
      .replace('%{phone}', data.customer.phone)

    this.vinHintTarget.innerHTML = `
      <div class="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 mt-2">
        <svg class="icon size-4 text-amber-600 shrink-0 mt-0.5"><use href="#icon-exclamation-triangle"></use></svg>
        <div class="text-xs">
          <p class="text-amber-800 dark:text-amber-200 font-medium">${this.escapeHTML(msg)}</p>
          <button type="button"
            class="mt-1 text-xs font-semibold text-primary underline hover:no-underline"
            data-action="click->appointment-form#transferVinVehicle"
            data-vehicle-id="${data.vehicle.id}"
            data-customer-name="${this.escapeHTML(data.customer.full_name)}">${this.escapeHTML(this.textVinTransferValue)}</button>
        </div>
      </div>`
  }

  transferVinVehicle (event) {
    event.preventDefault()
    const vehicleId = event.currentTarget.dataset.vehicleId
    if (!vehicleId || !/^\d+$/.test(vehicleId)) return
    const phone = this.phoneFieldTarget.value.trim()
    const url = `/admin/vehicles/${vehicleId}/transfer_form?phone=${encodeURIComponent(phone)}`

    const modalFrame = document.querySelector('turbo-frame#modal')
    if (modalFrame) {
      modalFrame.src = url
    }
  }

  clearVinHint () {
    if (this.hasVinHintTarget) {
      this.vinHintTarget.innerHTML = ''
    }
  }

  onServicesChange () {
    this.recalculateEndTime()
  }

  onStartsAtChange () {
    this.recalculateEndTime()
  }

  onEndsAtChange () {
    this.checkBreakOverlapFromFields()
  }

  recalculateEndTime () {
    const startsAt = this.startsAtFieldTarget.value
    if (!startsAt) return

    const selectedOptions = this.servicesSelectTarget.selectedOptions
    let totalMinutes = 0

    for (const option of selectedOptions) {
      const duration = parseInt(option.dataset.duration, 10)
      if (!isNaN(duration)) totalMinutes += duration
    }

    const [hours, minutes] = startsAt.split(':').map(Number)
    const startMinutes = hours * 60 + minutes

    if (totalMinutes > 0) {
      const endMinutes = startMinutes + totalMinutes
      const endHours = Math.floor(endMinutes / 60)
      const endMins = endMinutes % 60
      this.endsAtFieldTarget.value = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
    }

    this.checkBreakOverlapFromFields()
  }

  checkBreakOverlapFromFields () {
    const startsAt = this.startsAtFieldTarget.value
    const endsAt = this.endsAtFieldTarget.value
    if (!startsAt || !endsAt) return

    const [sh, sm] = startsAt.split(':').map(Number)
    const [eh, em] = endsAt.split(':').map(Number)
    this.checkBreakOverlap(sh * 60 + sm, eh * 60 + em)
  }

  checkBreakOverlap (startMinutes, endMinutes) {
    if (!this.hasBreakHintTarget) return
    if (!this.breakStartsAtValue || !this.breakEndsAtValue) {
      this.breakHintTarget.classList.add('hidden')
      return
    }

    const [bsH, bsM] = this.breakStartsAtValue.split(':').map(Number)
    const [beH, beM] = this.breakEndsAtValue.split(':').map(Number)
    const breakStart = bsH * 60 + bsM
    const breakEnd = beH * 60 + beM

    if (startMinutes < breakEnd && endMinutes > breakStart) {
      this.breakHintTarget.textContent = this.breakWarningValue
      this.breakHintTarget.classList.remove('hidden')
    } else {
      this.breakHintTarget.classList.add('hidden')
    }
  }

  renderSidePanel (data) {
    if (!data.appointments || data.appointments.length === 0) {
      this.sidePanelContentTarget.innerHTML = this.emptySidePanelHTML(
        this.textVisitHistoryValue,
        this.textNoVisitsValue
      )
      return
    }

    let html = '<div class="rounded-xl border overflow-hidden">'
    html += `<div class="flex items-center gap-2.5 px-4 py-3" style="background: var(--foreground);">`
    html += `<h4 class="text-sm font-semibold" style="color: var(--primary);">${this.escapeHTML(this.textVisitHistoryValue)}</h4>`
    html += '</div>'
    html += '<div class="divide-y divide-border bg-card">'

    data.appointments.forEach(appt => {
      const statusClass = appt.status === 'completed' ? 'badge--success' : 'badge--default'

      html += `
        <div>
          <a href="${this.escapeHTML(appt.url)}" data-turbo-frame="modal"
             class="block px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer no-underline">
            <div class="flex justify-between items-start mb-1">
              <span class="text-sm font-medium text-foreground">${this.escapeHTML(appt.date)}</span>
              <span class="badge badge--sm ${statusClass}">${this.escapeHTML(appt.status)}</span>
            </div>
            <p class="text-sm text-muted-foreground">${this.escapeHTML(appt.services)}</p>
            <p class="text-xs text-muted-foreground">${this.escapeHTML(appt.mechanic)}</p>
            ${appt.products && appt.products.length > 0
              ? `<p class="text-xs text-muted-foreground/70 mt-1 leading-snug">${appt.products.map(p => `${this.escapeHTML(p.name)} ×${p.quantity}`).join(', ')}</p>`
              : ''}
          </a>
          ${appt.products && appt.products.length > 0
            ? `<div style="padding:0 16px 8px;">
                <button type="button"
                  data-action="click->appointment-form#repeatProducts"
                  data-products="${this.escapeHTML(JSON.stringify(appt.products))}"
                  style="font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;background:oklch(from var(--primary) l c h / 15%);color:oklch(from var(--primary) calc(l - 0.35) c h);border:1px solid oklch(from var(--primary) l c h / 25%);cursor:pointer;display:inline-flex;align-items:center;gap:4px;">
                  ${this.escapeHTML(this.textRepeatProductsValue)}
                </button>
              </div>`
            : ''}
        </div>`
    })

    html += '</div></div>'
    this.sidePanelContentTarget.innerHTML = html
  }

  renderEmptySidePanel () {
    this.sidePanelContentTarget.innerHTML = this.emptySidePanelHTML(
      this.textNewCustomerTitleValue,
      this.textNewCustomerDescriptionValue
    )
  }

  emptySidePanelHTML (title, description) {
    return `
      <div class="empty">
        <div class="empty__icon"><svg class="icon size-12"><use href="#icon-clock"></use></svg></div>
        <h3 class="empty__title">${this.escapeHTML(title)}</h3>
        <p class="empty__description">${this.escapeHTML(description)}</p>
      </div>`
  }

  repeatProducts (event) {
    event.stopPropagation()
    try {
      const products = JSON.parse(event.currentTarget.dataset.products)
      document.dispatchEvent(new CustomEvent('repeat-products', { detail: { products } }))
    } catch (e) { /* invalid JSON — ignore */ }
  }

  // "10:00:00.000" or "10:00:00" → "10:00"
  _toHHMM (val) {
    if (!val) return ''
    return val.replace(/^(\d{1,2}:\d{2}).*/, '$1')
  }

  // Set field value programmatically (overrides any manual input on decode).
  _setReadonlyField (field, value) {
    field.value = value
  }

  escapeHTML (str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  async openVinCamera () {
    if (!this.hasVinCameraModalTarget) return

    this.vinCameraModalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
    this.#setVinCameraStatus('Запрашиваем доступ к камере…', false)
    this.vinCaptureBtnTarget.disabled = true
    this.vinCaptureBtnTarget.classList.add('opacity-40')

    if (!navigator.mediaDevices?.getUserMedia) {
      this.#setVinCameraStatus('⚠ Камера не поддерживается браузером.\nИспользуйте Chrome или Safari.', true)
      return
    }

    try {
      this.#vinStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      })
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? '⚠ Доступ к камере запрещён.\nРазрешите доступ в настройках браузера.'
        : `⚠ Не удалось открыть камеру.\n(${err.name})`
      this.#setVinCameraStatus(msg, true)
      return
    }

    const video = this.vinCameraVideoTarget
    video.srcObject = this.#vinStream
    try { await video.play() } catch {
      this.#setVinCameraStatus('⚠ Не удалось воспроизвести видео.', true)
      return
    }

    this.#setVinCameraStatus(null)
    this.vinCaptureBtnTarget.disabled = false
    this.vinCaptureBtnTarget.classList.remove('opacity-40')
    this.#startVinOverlay()
  }

  closeVinCamera () {
    this.#stopVinStream()
    if (this.hasVinCameraModalTarget) {
      this.vinCameraModalTarget.classList.add('hidden')
    }
    document.body.style.overflow = ''
  }

  async captureVinFrame () {
    const video  = this.vinCameraVideoTarget
    const canvas = this.vinCaptureCanvasTarget
    canvas.width  = video.videoWidth  || 1280
    canvas.height = video.videoHeight || 720
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
    const base64 = canvas.toDataURL('image/jpeg', 0.92)

    this.closeVinCamera()
    const isChassis = this.vinMode === 'chassis'
    const label = isChassis ? 'кузов' : 'VIN'
    this.vinHintTarget.textContent = `Распознаём ${label} с фото…`
    this.vinHintTarget.className = 'form__hint text-muted-foreground'

    try {
      const response = await fetch(this.scanUrlValue, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ image: base64, mode: this.vinMode })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()

      if (data.vin) {
        this.vinFieldTarget.value = data.vin
        this.vinHintTarget.textContent = `${label} найден: ${data.vin}`
        this.vinHintTarget.className = 'form__hint text-green-600'
        this.onDecodeVin()
      } else {
        this.vinHintTarget.textContent = `${label} не распознан. Введите вручную.`
        this.vinHintTarget.className = 'form__hint text-red-500'
      }
    } catch {
      this.vinHintTarget.textContent = 'Ошибка распознавания. Попробуйте ещё раз.'
      this.vinHintTarget.className = 'form__hint text-red-500'
    }
  }

  #startVinOverlay () {
    const canvas = this.vinOverlayCanvasTarget
    const draw = () => {
      const W = canvas.offsetWidth
      const H = canvas.offsetHeight
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H }

      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, W, H)

      const frameW = Math.min(W * 0.85, 600)
      const frameH = 80
      const frameX = (W - frameW) / 2
      const frameY = (H - frameH) / 2

      ctx.fillStyle = 'rgba(0,0,0,0.62)'
      ctx.fillRect(0, 0, W, H)
      ctx.clearRect(frameX, frameY, frameW, frameH)

      const primary = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() || '#6366f1'
      ctx.strokeStyle = `oklch(${primary})`
      ctx.lineWidth = 2
      ctx.strokeRect(frameX, frameY, frameW, frameH)

      const bLen = 28
      ctx.lineWidth = 4
      ctx.lineCap = 'square'
      for (const [cx, cy, dx, dy] of [
        [frameX, frameY, 1, 1], [frameX + frameW, frameY, -1, 1],
        [frameX, frameY + frameH, 1, -1], [frameX + frameW, frameY + frameH, -1, -1]
      ]) {
        ctx.beginPath()
        ctx.moveTo(cx + dx * bLen, cy)
        ctx.lineTo(cx, cy)
        ctx.lineTo(cx, cy + dy * bLen)
        ctx.stroke()
      }
      this.#vinOverlayRaf = requestAnimationFrame(draw)
    }
    this.#vinOverlayRaf = requestAnimationFrame(draw)
  }

  #stopVinStream () {
    if (this.#vinOverlayRaf) { cancelAnimationFrame(this.#vinOverlayRaf); this.#vinOverlayRaf = null }
    if (this.#vinStream) { this.#vinStream.getTracks().forEach(t => t.stop()); this.#vinStream = null }
    if (this.hasVinCameraVideoTarget) this.vinCameraVideoTarget.srcObject = null
  }

  #setVinCameraStatus (text, isError = false) {
    const el = this.vinCameraStatusTarget
    if (!text) { el.classList.add('hidden'); return }
    el.classList.remove('hidden')
    el.querySelector('[data-role="status-text"]').textContent = text
    el.querySelector('[data-role="status-icon"]').textContent = isError ? '⚠' : '⏳'
  }

  // ── Draft persistence (sessionStorage) ──

  get _draftKey () {
    return `appointment_draft_${this.scheduleIdValue || location.pathname}`
  }

  _saveDraft () {
    if (this._restoringDraft) return
    const data = {
      customerType: this.customerType,
      phone: this.hasPhoneFieldTarget ? this.phoneFieldTarget.value : '',
      firstName: this.hasFirstNameFieldTarget ? this.firstNameFieldTarget.value : '',
      lastName: this.hasLastNameFieldTarget ? this.lastNameFieldTarget.value : '',
      companyName: this.hasCompanyNameFieldTarget ? this.companyNameFieldTarget.value : '',
      vin: this.hasVinFieldTarget ? this.vinFieldTarget.value : '',
      make: this.hasMakeFieldTarget ? this.makeFieldTarget.value : '',
      model: this.hasModelFieldTarget ? this.modelFieldTarget.value : '',
      year: this.hasYearFieldTarget ? this.yearFieldTarget.value : '',
      mileage: this.hasMileageFieldTarget ? this.mileageFieldTarget.value : '',
      engineVolume: this.hasEngineVolumeFieldTarget ? this.engineVolumeFieldTarget.value : '',
      licensePlate: this.hasLicensePlateFieldTarget ? this.licensePlateFieldTarget.value : '',
      startsAt: this.hasStartsAtFieldTarget ? this._toHHMM(this.startsAtFieldTarget.value) : '',
      endsAt: this.hasEndsAtFieldTarget ? this._toHHMM(this.endsAtFieldTarget.value) : '',
      comment: this.hasFormTarget ? this.formTarget.querySelector('textarea[name*="comment"]')?.value || '' : '',
      vinMode: this.vinMode
    }

    // Save selected mechanic (user_id) — from select dropdown or hidden field
    if (this.hasFormTarget) {
      const mechanicField = this.formTarget.querySelector('select[name*="user_id"]') ||
                            this.formTarget.querySelector('input[name*="user_id"]')
      if (mechanicField) data.mechanicId = mechanicField.value
    }

    // Save selected services
    if (this.hasServicesSelectTarget) {
      const selected = Array.from(this.servicesSelectTarget.selectedOptions).map(o => o.value)
      data.serviceIds = selected
    }

    try {
      sessionStorage.setItem(this._draftKey, JSON.stringify(data))
    } catch (e) { /* quota exceeded — ignore */ }
  }

  _restoreDraft () {
    let data
    try {
      const raw = sessionStorage.getItem(this._draftKey)
      if (!raw) return
      data = JSON.parse(raw)
    } catch (e) { return }

    const isEmpty = !data.phone || data.phone.replace(/[^0-9]/g, '').length <= 1

    if (isEmpty) {
      this._clearDraft()
      return
    }

    this._restoringDraft = true

    // Restore phone
    if (this.hasPhoneFieldTarget && data.phone) {
      if (this.phoneMask) {
        this.phoneMask.value = data.phone
        this.phoneMask.updateValue()
      } else {
        this.phoneFieldTarget.value = data.phone
      }
    }

    // Restore customer type
    if (data.customerType && data.customerType !== this.customerType) {
      const btn = this.customerTypeBtnTargets.find(b => b.dataset.customerType === data.customerType)
      if (btn) btn.click()
    }

    // Restore name fields
    if (this.hasFirstNameFieldTarget && data.firstName) this.firstNameFieldTarget.value = data.firstName
    if (this.hasLastNameFieldTarget && data.lastName) this.lastNameFieldTarget.value = data.lastName
    if (this.hasCompanyNameFieldTarget && data.companyName) this.companyNameFieldTarget.value = data.companyName

    // Restore vehicle fields
    if (this.hasVinFieldTarget && data.vin) this.vinFieldTarget.value = data.vin
    if (this.hasMakeFieldTarget && data.make) this._setReadonlyField(this.makeFieldTarget, data.make)
    if (this.hasModelFieldTarget && data.model) this._setReadonlyField(this.modelFieldTarget, data.model)
    if (this.hasYearFieldTarget && data.year) this._setReadonlyField(this.yearFieldTarget, data.year)
    if (this.hasMileageFieldTarget && data.mileage) this.mileageFieldTarget.value = data.mileage
    if (this.hasEngineVolumeFieldTarget && data.engineVolume) this._setReadonlyField(this.engineVolumeFieldTarget, data.engineVolume)
    if (this.hasLicensePlateFieldTarget && data.licensePlate) this.licensePlateFieldTarget.value = data.licensePlate

    // Restore VIN mode
    if (data.vinMode && data.vinMode !== 'vin') {
      const btn = this.vinModeBtnTargets.find(b => b.dataset.mode === data.vinMode)
      if (btn) btn.click()
    }

    // Restore mechanic
    if (data.mechanicId && this.hasFormTarget) {
      const mechanicSelect = this.formTarget.querySelector('select[name*="user_id"]')
      if (mechanicSelect) mechanicSelect.value = data.mechanicId
    }

    // Restore services
    if (data.serviceIds?.length && this.hasServicesSelectTarget) {
      const select = this.servicesSelectTarget
      Array.from(select.options).forEach(opt => {
        opt.selected = data.serviceIds.includes(opt.value)
      })
      select.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Restore time fields
    if (this.hasStartsAtFieldTarget && data.startsAt) this.startsAtFieldTarget.value = data.startsAt
    if (this.hasEndsAtFieldTarget && data.endsAt) this.endsAtFieldTarget.value = data.endsAt

    // Restore comment
    if (data.comment && this.hasFormTarget) {
      const textarea = this.formTarget.querySelector('textarea[name*="comment"]')
      if (textarea) textarea.value = data.comment
    }

    // Trigger customer search to restore side panel, then re-apply draft fields
    // that searchCustomer overwrites (name, vehicle, etc.)
    const digits = (data.phone || '').replace(/\D/g, '')
    if (digits.length >= 6) {
      this._pendingDraft = data
      this.searchCustomer(data.phone).then(() => {
        this._reapplyDraftAfterSearch()
      }).catch(() => {
        this._reapplyDraftAfterSearch()
      })
    } else {
      this._restoringDraft = false
    }
  }

  _reapplyDraftAfterSearch () {
    const data = this._pendingDraft
    this._pendingDraft = null
    if (!data) {
      this._restoringDraft = false
      return
    }

    // Re-apply fields that fillCustomerData / resetCustomerFields overwrites
    if (this.hasFirstNameFieldTarget && data.firstName) this.firstNameFieldTarget.value = data.firstName
    if (this.hasLastNameFieldTarget && data.lastName) this.lastNameFieldTarget.value = data.lastName
    if (this.hasCompanyNameFieldTarget && data.companyName) this.companyNameFieldTarget.value = data.companyName

    // Re-apply vehicle fields
    if (this.hasVinFieldTarget && data.vin) this.vinFieldTarget.value = data.vin
    if (this.hasMakeFieldTarget && data.make) this._setReadonlyField(this.makeFieldTarget, data.make)
    if (this.hasModelFieldTarget && data.model) this._setReadonlyField(this.modelFieldTarget, data.model)
    if (this.hasYearFieldTarget && data.year) this._setReadonlyField(this.yearFieldTarget, data.year)
    if (this.hasMileageFieldTarget && data.mileage) this.mileageFieldTarget.value = data.mileage
    if (this.hasEngineVolumeFieldTarget && data.engineVolume) this._setReadonlyField(this.engineVolumeFieldTarget, data.engineVolume)
    if (this.hasLicensePlateFieldTarget && data.licensePlate) this.licensePlateFieldTarget.value = data.licensePlate

    // Re-apply VIN mode
    if (data.vinMode && data.vinMode !== 'vin') {
      const btn = this.vinModeBtnTargets.find(b => b.dataset.mode === data.vinMode)
      if (btn) btn.click()
    }

    this._restoringDraft = false
  }

  _clearDraft () {
    try { sessionStorage.removeItem(this._draftKey) } catch (e) {}
  }

  get csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}

stimulus.register('appointment-form', AppointmentFormController)
